import { Box, Text, CloseButton } from "@chakra-ui/react";
import moment from "moment";
import "moment/locale/es";
import React from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { DataContext } from "../DataContext";
import useWindowSize from "../useWindowSize";
import CalendarAgenda from "./CalendarAgenda";
import CalendarWeek from "./CalendarWeek";
import TabSystem from "./TabSystem";
import { getColor } from "../utils";

const localizer = momentLocalizer(moment);
const min = new Date().setHours(7, 0, 0);
const max = new Date().setHours(23, 30, 0);

const MateriaEvent = (props) => {
  const { removeExtraFromTab } = React.useContext(DataContext);
  return (
    <>
      {!props.event.curso && (
        <CloseButton
          float="right"
          mt="-20px"
          size="sm"
          onClick={(ev) => {
            ev.stopPropagation();
            removeExtraFromTab(props.event.id);
          }}
        />
      )}

      <Box>
        <Text noOfLines={[1, 2, 3]} className="rbc-agenda-event-cell" mb={2}>
          {props.event.title}
        </Text>
        <Text noOfLines={[1, 3, 5]} className="rbc-agenda-event-cell-sub">
          {props.event.subtitle}
        </Text>
      </Box>
    </>
  );
};

const MateriaEventAgenda = (props) => {
  return (
    <Box>
      <Box
        style={{
          display: "flex",
          flexDirection: "row",
        }}
      >
        <Text noOfLines={[1, 2, 3]} className="rbc-agenda-event-cell" mb={2}>
          {props.event.title}
        </Text>
      </Box>
      <Text noOfLines={[1, 3, 5]} className="rbc-agenda-event-cell-sub">
        {props.event.subtitle}
      </Text>
    </Box>
  );
};

const MyCalendar = (props) => {
  const { events, useAgenda } = props;
  const { width } = useWindowSize();
  const { addExtra, isCursoIgnorado } = React.useContext(DataContext);

  const eventPropsGetter = React.useCallback(
    (event) => {
      let color = event.color ?? (event.id ? getColor(event) : "inherit");
      const style = {
        borderStyle: "solid",
        borderWidth: "1px 1px 1px 6px",
        borderRightColor: "#d2adf4", //primary.300
        borderBottomColor: "#d2adf4", //primary.300
        borderTopColor: "#d2adf4", //primary.300
        borderLeftColor: color,
        color: "#1f1f1f",
        cursor: "default",
      };

      if (useAgenda && event.isPlaceholder) {
        return {
          className: "rbc-agenda-placeholder-event",
          style: {
            ...style,
            borderLeftColor: "#d2adf4",
          },
        };
      }

      const day = event.start?.getDay?.();
      const isIgnored =
        !!event.curso &&
        isCursoIgnorado(event.curso, day, event.start, event.end);

      const eventFillStyle = isIgnored
        ? {
            // Stripes diagonales con un fondo negro con 10% de opacidad.
            // Es mejor tener un fondo negro que del propio color de la materia en las stripes para mantener la visibilidad en todos los colores.
            backgroundImage: `linear-gradient(${color}44, ${color}44), repeating-linear-gradient(45deg, #0000001A 0px, #0000001A 8px, transparent 8px, transparent 16px)`,
            backgroundColor: "#FFF",
          }
        : {
            boxShadow: "inset 0 0 0 1000px " + color + "44",
            backgroundColor: "#FFF",
          };
      const calendarWeekStyle = {
        textAlign: "right",
        borderRightColor: "#0000",
        borderBottomColor: "#0000",
        borderTopColor: "#0000",
        ...eventFillStyle
      };
      return {
        style: useAgenda
          ? { ...style, ...eventFillStyle }
          : { ...style, ...calendarWeekStyle },
      };
    },
    [useAgenda, isCursoIgnorado],
  );

  const coveredDays = events.map((e) => e.start.getDay());
  const notCoveredDays = [1, 2, 3, 4, 5].filter(
    (d) => !coveredDays.includes(d),
  );
  const dummyEvents = notCoveredDays.map((i) => ({
    start: new Date(2018, 0, i, 7),
    end: new Date(2018, 0, i, 23, 30),
    title: "",
    subtitle: "",
    isPlaceholder: true,
  }));

  const formats = {
    dayFormat: (d) => {
      const f = d
        .toLocaleString("es-AR", {
          weekday: "long",
        })
        .toUpperCase();
      return width > 1180 ? f : f[0];
    },
    agendaDateFormat: (d) => {
      return d
        .toLocaleString("es-AR", {
          weekday: "long",
        })
        .toUpperCase();
    },
    timeGutterFormat: "HH:mm",
  };

  return (
    <Calendar
      selectable
      formats={formats}
      onView={() => {}}
      view={useAgenda ? "calendarAgenda" : "calendarWeek"}
      views={{ calendarAgenda: CalendarAgenda, calendarWeek: CalendarWeek }}
      localizer={localizer}
      min={min}
      max={max}
      defaultDate={new Date(2018, 0, 1)} // Monday
      events={useAgenda ? [...events, ...dummyEvents] : events}
      eventPropGetter={eventPropsGetter}
      components={{
        event: useAgenda ? MateriaEventAgenda : MateriaEvent,
        toolbar: TabSystem,
      }}
      onSelectSlot={addExtra}
      dayLayoutAlgorithm="no-overlap"
      tooltipAccessor="tooltip"
    />
  );
};

export default MyCalendar;
